import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAdminAppearance } from '../../contexts/AdminAppearanceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { isNoDyeProblem } from '../../utils/problemTypeDisplay';
import { matchesBlinkingProblemType } from '../../utils/blinkingProblemTypes';
import { formatKg, isSectionHeaderRow, sectionTotalWeightsKg } from '../../utils/productionKg';
import { canMarkToRewind } from '../../utils/rewindEntries';
import '../../styles/global.css';

interface ProductionItem {
  id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  spool_length_m: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  /** 'available' | 'missing' | null — dye availability marked by a person. */
  barwnik_status?: 'available' | 'missing' | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  ean: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  uwagi: string | null;
  kolejnosc?: number | null;
  rewind_entries?: Array<{ id: number; przewinieta: boolean; marked_to_rewind?: boolean }>;
}

interface AssignmentData {
  assignment_id: number;
  week: string;
  status: 'pending' | 'in_progress' | 'completed' | 'problem_bb' | 'problem_awaria' | 'niepelna_produkcja';
  notes: string | null;
  notatka?: string | null;
  user: {
    id: number;
    name: string;
    email: string;
  } | null;
}

interface ItemData {
  item: ProductionItem;
  assignments: AssignmentData[];
}

interface ProductionLineViewData {
  production_line: {
    id: number;
    name: string;
    spool_type?: 'small' | 'big';
  };
  items: ItemData[];
  item_status_counts: {
    pending: number;
    in_progress: number;
    completed: number;
  };
  /** Realizacja bieżącego zakresu (cała linia albo wybrany tydzień) liczona w kilogramach. */
  completion?: {
    planned_kg: number;
    produced_kg: number;
    percent: number;
  };
  kg_counts: {
    oczekujace: number;
    w_trakcie: number;
    zakonczone: number;
  };
  available_weeks: { week: string; is_closed: boolean; is_empty: boolean }[];
}

const EDITABLE_FIELDS: Array<keyof ProductionItem> = [
  'linia', 'tydzien', 'material', 'srednica', 'waga', 'spool_length_m', 'szpula',
  'kolor', 'barwnik', 'doz_procent', 'marka', 'nazwa_oryg',
  'indeks_kat', 'do_produkcji', 'ean', 'uwagi',
];

const ProductionLineView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const weekFromUrl = searchParams.get('week') || '';
  const { columnWidthLeft } = useAdminAppearance();
  const { t } = useLanguage();
  const { isManager, isAdmin, isTracker } = useAuth();
  const [data, setData] = useState<ProductionLineViewData | null>(null);
  const [filterWeek, setFilterWeek] = useState(weekFromUrl);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [editingNotes, setEditingNotes] = useState<{ itemId: number; assignmentId: number } | null>(null);
  const [selectedProblemTypeId, setSelectedProblemTypeId] = useState<number | null>(null);
  const [notatkiList, setNotatkiList] = useState<Array<{ id: number; tresc: string; created_at: string; user?: { id: number; name: string } | null }>>([]);
  const [newNotatka, setNewNotatka] = useState('');
  const [savingNotatka, setSavingNotatka] = useState(false);
  const [problemTypes, setProblemTypes] = useState<Array<{ id: number; name: string; description: string | null; is_active?: boolean; is_blinking?: boolean }>>([]);
  const [notesHistory, setNotesHistory] = useState<any[]>([]);
  const [loadingNotesHistory, setLoadingNotesHistory] = useState(false);

  // EAN management
  const [itemEans, setItemEans] = useState<Array<{ id: number; ean: string }>>([]);
  const [newEanInput, setNewEanInput] = useState('');
  const [eanSaving, setEanSaving] = useState(false);
  const [eanError, setEanError] = useState<string | null>(null);

  // Status change modals
  const [showStartModal, setShowStartModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ item: ProductionItem; assignmentId: number } | null>(null);
  const [selectedShift, setSelectedShift] = useState<'zmiana_1' | 'zmiana_2'>('zmiana_1');
  const [incompleteQuantity, setIncompleteQuantity] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductionItem | null>(null);
  const [editForm, setEditForm] = useState<Partial<Record<keyof ProductionItem, string>>>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [productionLines, setProductionLines] = useState<Array<{ id: number; name: string }>>([]);

  // Delete (admin only): step 1 = PIN, step 2 = confirm
  const [deletingItem, setDeletingItem] = useState<ProductionItem | null>(null);
  const [deleteStep, setDeleteStep] = useState<'pin' | 'confirm'>('pin');
  const [deletePin, setDeletePin] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editAvailableWeeks, setEditAvailableWeeks] = useState<string[]>([]);
  const [editWeeksLoading, setEditWeeksLoading] = useState(false);

  // Reordering (strzałki ↑/↓) of rows in the line view
  const [reorderMode, setReorderMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    setFilterWeek(weekFromUrl);
  }, [weekFromUrl]);

  useEffect(() => {
    if (id) {
      fetchLineView();
    }
  }, [id, filterWeek]);

  useEffect(() => {
    fetchProblemTypes();
  }, []);

  const fetchProblemTypes = async () => {
    try {
      const response = await api.get('/problem-types');
      setProblemTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching problem types:', error);
    }
  };

  const fetchNotesHistory = async (assignmentId: number, itemId: number) => {
    setLoadingNotesHistory(true);
    try {
      const response = await api.get(`/production-assignments/${assignmentId}/items/${itemId}/notes-history`);
      setNotesHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching notes history:', error);
      setNotesHistory([]);
    } finally {
      setLoadingNotesHistory(false);
    }
  };

  const fetchItemEans = async (itemId: number) => {
    try {
      const res = await api.get(`/production-items/${itemId}/eans`);
      setItemEans(res.data.eans || []);
    } catch {
      setItemEans([]);
    }
  };

  const handleAddEan = async () => {
    if (!editingNotes || !newEanInput.trim()) return;
    setEanSaving(true);
    setEanError(null);
    try {
      const res = await api.post(`/production-items/${editingNotes.itemId}/eans`, { ean: newEanInput.trim() });
      setItemEans(prev => [...prev, res.data.ean]);
      setNewEanInput('');
    } catch (err: any) {
      setEanError(err?.response?.data?.message || 'Błąd podczas dodawania EAN');
    } finally {
      setEanSaving(false);
    }
  };

  const handleDeleteEan = async (eanId: number) => {
    try {
      await api.delete(`/production-item-eans/${eanId}`);
      setItemEans(prev => prev.filter(e => e.id !== eanId));
    } catch {
      // silently ignore
    }
  };

  const fetchWeeksForLineName = async (
    lineName: string,
    lines: Array<{ id: number; name: string }>,
  ): Promise<string[]> => {
    const trimmed = (lineName ?? '').trim();
    if (!trimmed) {
      setEditAvailableWeeks([]);
      return [];
    }
    const line = lines.find(l => l.name === trimmed);
    if (!line) {
      setEditAvailableWeeks([]);
      return [];
    }
    setEditWeeksLoading(true);
    try {
      const response = await api.get(`/production-lines/${line.id}/weeks`);
      const weeks: string[] = response.data?.weeks || [];
      setEditAvailableWeeks(weeks);
      return weeks;
    } catch {
      setEditAvailableWeeks([]);
      return [];
    } finally {
      setEditWeeksLoading(false);
    }
  };

  const openEditItem = async (itemData: ItemData) => {
    const item = itemData.item;
    setEditingItem(item);
    const initial: Partial<Record<keyof ProductionItem, string>> = {};
    EDITABLE_FIELDS.forEach((f) => {
      initial[f] = (item[f] ?? '') as string;
    });
    // Override linia/tydzien with the actual assignment values — these are the
    // source of truth for what the item is currently assigned to. The item's
    // own `linia`/`tydzien` columns come from the imported Excel and may not
    // match any real production line.
    const assignedLineName = data?.production_line?.name ?? '';
    const assignedWeek = itemData.assignments[0]?.week ?? '';
    if (assignedLineName) initial.linia = assignedLineName;
    if (assignedWeek) initial.tydzien = assignedWeek;
    setEditForm(initial);
    setEditError(null);
    setEditAvailableWeeks([]);
    setShowEditModal(true);
    let lines: Array<{ id: number; name: string }> = [];
    try {
      const response = await api.get('/production-lines');
      lines = response.data || [];
      setProductionLines(lines);
    } catch {
      setProductionLines([]);
    }
    const lineForWeeks = assignedLineName || item.linia || '';
    if (lineForWeeks) {
      await fetchWeeksForLineName(lineForWeeks, lines);
    }
  };

  const handleEditFieldChange = (field: keyof ProductionItem, value: string) => {
    if (field === 'linia') {
      setEditForm(prev => ({ ...prev, linia: value, tydzien: '' }));
      fetchWeeksForLineName(value, productionLines);
      return;
    }
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    if (!editForm.linia || String(editForm.linia).trim() === '') {
      setEditError(t('productionItemEdit.liniaRequired'));
      return;
    }
    if (!editForm.tydzien || String(editForm.tydzien).trim() === '') {
      setEditError(t('productionItemEdit.tydzienRequired'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const payload: Record<string, string | null> = {};
      EDITABLE_FIELDS.forEach((f) => {
        const value = editForm[f];
        payload[f] = value === '' || value === undefined ? null : value;
      });
      await api.patch(`/production-items/${editingItem.id}`, payload);

      setShowEditModal(false);
      setEditingItem(null);
      await fetchLineView();
    } catch (err: any) {
      setEditError(err?.response?.data?.message || t('common.errorOccurred'));
    } finally {
      setEditSaving(false);
    }
  };

  const openDeleteItem = (item: ProductionItem) => {
    setDeletingItem(item);
    setDeleteStep('pin');
    setDeletePin('');
    setDeleteError(null);
  };

  const closeDeleteItem = () => {
    setDeletingItem(null);
    setDeleteStep('pin');
    setDeletePin('');
    setDeleteError(null);
    setDeleting(false);
  };

  const handleDeletePinNext = () => {
    if (deletePin.trim().length !== 6) {
      setDeleteError(t('productionItemDelete.pinInvalid'));
      return;
    }
    setDeleteError(null);
    setDeleteStep('confirm');
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/production-items/${deletingItem.id}`, { data: { pin: deletePin.trim() } });
      closeDeleteItem();
      await fetchLineView();
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('common.errorOccurred');
      setDeleteError(msg);
      if (err?.response?.status === 422) {
        setDeleteStep('pin');
      }
      setDeleting(false);
    }
  };

  const handleNotesClick = async (itemData: ItemData) => {
    if (!itemData.item.do_produkcji || String(itemData.item.do_produkcji).trim() === '') return;
    const assignmentId = itemData.assignments[0]?.assignment_id;
    if (!assignmentId) return;

    setEditingNotes({ itemId: itemData.item.id, assignmentId });

    const currentNotes = itemData.assignments.find(a => a.notes)?.notes || '';
    const matchingProblemType = problemTypes.find(pt => pt.name === currentNotes);
    setSelectedProblemTypeId(matchingProblemType?.id || null);
    setNewNotatka('');

    setNewEanInput('');
    setEanError(null);
    setShowNotesModal(true);
    await Promise.all([
      fetchNotesHistory(assignmentId, itemData.item.id),
      fetchItemEans(itemData.item.id),
      fetchNotatki(assignmentId, itemData.item.id),
    ]);
  };

  const fetchNotatki = async (assignmentId: number, itemId: number) => {
    try {
      const res = await api.get(`/production-assignments/${assignmentId}/items/${itemId}/notatki`);
      setNotatkiList(res.data || []);
    } catch {
      setNotatkiList([]);
    }
  };

  const handleAddNotatka = async () => {
    if (!editingNotes || !newNotatka.trim()) return;
    setSavingNotatka(true);
    try {
      await api.post(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notatki`, {
        tresc: newNotatka.trim(),
      });
      setNewNotatka('');
      await fetchNotatki(editingNotes.assignmentId, editingNotes.itemId);
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || t('common.errorOccurred')));
    } finally {
      setSavingNotatka(false);
    }
  };

  const handleDeleteNotatka = async (notatkaId: number) => {
    if (!editingNotes) return;
    try {
      await api.delete(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notatki/${notatkaId}`);
      await fetchNotatki(editingNotes.assignmentId, editingNotes.itemId);
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || t('common.errorOccurred')));
    }
  };

  const handleSaveNotes = async () => {
    if (!editingNotes || !selectedProblemTypeId) {
      alert('Proszę wybrać problem z listy');
      return;
    }

    try {
      await api.put(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notes`, {
        problem_type_id: selectedProblemTypeId,
      });
      setShowNotesModal(false);
      setEditingNotes(null);
      setSelectedProblemTypeId(null);
      setNotesHistory([]);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się zapisać uwagi'));
    }
  };

  const handleDeleteNotes = async () => {
    if (!editingNotes) return;
    if (!window.confirm('Czy na pewno usunąć uwagę?')) return;
    try {
      await api.put(`/production-assignments/${editingNotes.assignmentId}/items/${editingNotes.itemId}/notes`, {
        problem_type_id: null,
        note: null,
      });
      setShowNotesModal(false);
      setEditingNotes(null);
      setSelectedProblemTypeId(null);
      setNotesHistory([]);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się usunąć uwagi'));
    }
  };

  // Remarks whose problem type has the "blinking" option enabled — items carrying
  // one of them pulse in the table.
  const blinkingProblemTypeNames = problemTypes.filter(pt => pt.is_blinking).map(pt => pt.name);

  const getItemStatus = (itemData: ItemData): string => {
    const statuses = itemData.assignments.map(a => a.status);
    if (statuses.includes('completed')) return 'completed';
    if (statuses.includes('in_progress')) return 'in_progress';
    if (statuses.includes('problem_bb')) return 'problem_bb';
    if (statuses.includes('problem_awaria')) return 'problem_awaria';
    if (statuses.includes('niepelna_produkcja')) return 'niepelna_produkcja';
    return 'pending';
  };

  const handleRowClick = (itemData: ItemData) => {
    // Konto podgladowe (Tracker) tylko patrzy — backend i tak odrzucilby zapis, wiec nie ma
    // po co otwierac okien, ktore koncza sie bledem.
    if (isTracker) return;
    if (!itemData.item.do_produkcji || String(itemData.item.do_produkcji).trim() === '') return;
    const assignmentId = itemData.assignments[0]?.assignment_id;
    if (!assignmentId) return;
    const currentStatus = getItemStatus(itemData);
    const itemWithAssignment = { item: itemData.item, assignmentId };

    if (currentStatus === 'pending') {
      setSelectedItem(itemWithAssignment);
      setShowStartModal(true);
    } else if (currentStatus === 'in_progress') {
      setSelectedItem(itemWithAssignment);
      setShowCompleteModal(true);
    } else if (currentStatus === 'completed' || currentStatus === 'problem_bb' || currentStatus === 'problem_awaria' || currentStatus === 'niepelna_produkcja') {
      setSelectedItem(itemWithAssignment);
      setShowCancelModal(true);
    }
  };

  // Persist the current order of data.items to the backend.
  const persistOrder = async (orderedItems: ItemData[]) => {
    setSavingOrder(true);
    try {
      const itemIds = orderedItems.map((it) => it.item.id);
      await api.put('/production-items/reorder', { item_ids: itemIds });
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.reorderSaveError')}`);
      // Reload to recover the server-side order on failure
      fetchLineView();
    } finally {
      setSavingOrder(false);
    }
  };

  // Move a row up (-1) or down (+1) by one position and persist the new order.
  const moveItem = (index: number, direction: -1 | 1) => {
    if (!data || savingOrder) return;
    const target = index + direction;
    if (target < 0 || target >= data.items.length) return;
    const items = [...data.items];
    [items[index], items[target]] = [items[target], items[index]];
    setData({ ...data, items });
    persistOrder(items);
  };

  const handleStartConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: 'in_progress',
      });
      setShowStartModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się rozpocząć produkcji'));
    }
  };

  const handleCompleteConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: 'completed',
        shift: selectedShift,
      });
      setShowCompleteModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się zakończyć produkcji'));
    }
  };

  const handleProblemOption = async (option: 'continue' | 'incomplete' | 'problem_bb' | 'problem_awaria') => {
    if (!selectedItem) return;
    if (option === 'continue') {
      setShowProblemModal(false);
      setSelectedItem(null);
      return;
    }
    if (option === 'incomplete') {
      setShowProblemModal(false);
      setShowIncompleteModal(true);
      return;
    }
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: option,
      });
      setShowProblemModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się zaktualizować statusu'));
    }
  };

  const handleIncompleteSave = async () => {
    if (!selectedItem || !incompleteQuantity || parseInt(incompleteQuantity) < 0) {
      alert('Musisz podać poprawną ilość');
      return;
    }
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: 'niepelna_produkcja',
        wykonano_ilosc: parseInt(incompleteQuantity),
        shift: selectedShift,
      });
      setShowIncompleteModal(false);
      setSelectedItem(null);
      setIncompleteQuantity('');
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się zapisać'));
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: 'pending',
        cancel_production: true,
      });
      setShowCancelModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się anulować'));
    }
  };

  // Route a completed (green) item to the rewinding station post factum. Used to fix a
  // wrongly reported over-plan surplus: creating a pending rewind entry excludes the item
  // from the material surplus and turns the row blue until it is rewound at the station.
  const handleAddToRewinding = async () => {
    if (!selectedItem) return;
    try {
      await api.post('/warehouse/large/add', {
        production_item_id: selectedItem.item.id,
        production_assignment_id: selectedItem.assignmentId,
      });
      setShowCancelModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert(`${t('common.error')}: ${error.response?.data?.message || t('myProduction.unknownError')}`);
    }
  };

  const handleRemoveProblem = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/production-assignments/${selectedItem.assignmentId}/items/${selectedItem.item.id}/status`, {
        status: 'in_progress',
      });
      setShowCancelModal(false);
      setSelectedItem(null);
      fetchLineView();
    } catch (error: any) {
      alert('Błąd: ' + (error.response?.data?.message || 'Nie udało się usunąć problemu'));
    }
  };

  const fetchLineView = async () => {
    try {
      setLoading(true);
      const url = `/admin/production-lines/${id}/view${filterWeek ? `?week=${filterWeek}` : ''}`;
      const response = await api.get<ProductionLineViewData>(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching line view:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kolejność pierwszeństwa jest celowo taka sama jak w getRowColor w "Mojej produkcji",
  // żeby podgląd pokazywał dokładnie ten sam kolor co linia. Zmieniając jedno, zmień drugie.
  const getRowColor = (itemData: ItemData): string => {
    const statuses = itemData.assignments.map(a => a.status);
    const isCompleted = statuses.includes('completed');

    const doProdukcji = itemData.item.do_produkcji ? parseInt(itemData.item.do_produkcji) : 0;
    const zmiana1 = itemData.item.zmiana_1 ? parseInt(itemData.item.zmiana_1) : 0;
    const zmiana2 = itemData.item.zmiana_2 ? parseInt(itemData.item.zmiana_2) : 0;
    const wyprodukowano = zmiana1 + zmiana2;

    // "Brak barwnika" podświetla tylko komórkę Barwnik, nie cały wiersz
    const hasNotes = itemData.assignments.some(a => a.notes && !isNoDyeProblem(a.status, a.notes))
      || (!!itemData.item.uwagi && !isNoDyeProblem(null, itemData.item.uwagi));

    // Pozycja w CAŁOŚCI przewinięta jest gotowa → zielona (i nie miga), niezależnie od uwagi
    // czy niepełnej ilości. Przewinięcie to ostatni etap — potem wiersz nie ma już alarmować.
    // Musi być PRZED czerwonym z uwagi, inaczej przewinięta pozycja z uwagą dalej migałaby.
    const rewindAll = Array.isArray(itemData.item.rewind_entries) ? itemData.item.rewind_entries : [];
    if (rewindAll.length > 0 && !rewindAll.some((e) => !e.przewinieta)) return '#d1fae5';

    // Red if has notes and not fully produced (pozycje zamknięte nigdy nie są czerwone)
    if (hasNotes && wyprodukowano < doProdukcji && !isCompleted) return '#f8d7da';

    // Przewijanie ma pierwszeństwo nad "w pełni wyprodukowane": pozycja oznaczona jako
    // "do przewinięcia" (niebieski) pozostaje niebieska nawet gdy nawijanie jest już
    // ukończone – aż zostanie w całości przewinięta.
    //   niebieski  = gotowe/w trakcie przewijania (jest wpis oczekujący na przewinięcie)
    //   zielony    = wszystko przewinięte
    const rewindEntries = Array.isArray(itemData.item.rewind_entries) ? itemData.item.rewind_entries : [];
    // Niebieski dopiero po ręcznym oznaczeniu przyciskiem „Do przewijania" (marked_to_rewind).
    // Szpula wysłana na stację automatycznie po przekroczeniu progu wagi czeka tam, ale wiersz
    // na linii nie zmienia koloru, dopóki operator jej nie oznaczy.
    if (rewindEntries.some((e) => !e.przewinieta && e.marked_to_rewind)) return '#cfe2ff'; // niebieski
    if (rewindEntries.some((e) => e.przewinieta)) return '#d1fae5'; // zielony (przewinięte)

    // Na liniach dużych szpul zakończenie nawijania (osiągnięcie planu) NIE oznacza pozycji
    // jako zrealizowanej — czeka na przewinięcie. Zielony przez "w pełni wyprodukowane" dotyczy
    // tylko linii małych szpul; duże stają się zielone dopiero po przewinięciu (wyżej).
    const isBigSpoolLine = data?.production_line?.spool_type === 'big';
    if (!isBigSpoolLine && doProdukcji > 0 && wyprodukowano >= doProdukcji) return '#d1fae5';

    // Pozycja zamknięta (zakończona) zawsze na zielono
    if (isCompleted) return '#d1fae5';

    if (statuses.includes('problem_awaria')) return '#f8d7da';
    if (statuses.includes('niepelna_produkcja')) return '#fff3cd';
    if (statuses.includes('in_progress')) return '#fef3c7';
    return 'white';
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Oczekujące';
      case 'in_progress':
        return 'W trakcie';
      case 'completed':
        return 'Zakończone';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#e2e8f0';
      case 'in_progress':
        return '#fef3c7';
      case 'completed':
        return '#d1fae5';
      default:
        return '#e2e8f0';
    }
  };

  const getStatusTextColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#4a5568';
      case 'in_progress':
        return '#92400e';
      case 'completed':
        return '#065f46';
      default:
        return '#4a5568';
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="empty-state">
        <p>Brak danych</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>📊 Podgląd Linii Produkcyjnej: {data.production_line.name}</h1>
        <button onClick={() => navigate('/admin')} className="btn btn-outline">
          ← Powrót do Panelu
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Oczekujące (szt.)</h3>
          <p>{data.item_status_counts.pending}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
          <h3>W trakcie (szt.)</h3>
          <p>{data.item_status_counts.in_progress}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
          <h3>Zakończone (szt.)</h3>
          <p>{data.item_status_counts.completed}</p>
        </div>
      </div>
      <div className="stats-grid" style={{ marginTop: '12px' }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)' }}>
          <h3>Oczekujące (kg)</h3>
          <p>{data.kg_counts?.oczekujace?.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
          <h3>W trakcie (kg)</h3>
          <p>{data.kg_counts?.w_trakcie?.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #059669 0%, #34d399 100%)' }}>
          <h3>Zakończone (kg)</h3>
          <p>{data.kg_counts?.zakonczone?.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00'}</p>
        </div>
      </div>

      {/* Realizacja listy w kilogramach — wszystkie zaplanowane kg tej linii (i tygodnia,
          jeśli wybrany) to 100%. Widoczne tylko dla administratora. */}
      {isAdmin && (data.completion?.planned_kg ?? 0) > 0 && (
        <div style={{
          marginTop: '12px', marginBottom: '20px', padding: '14px 16px',
          backgroundColor: 'white', border: '2px solid #e1e8ed', borderRadius: '8px',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            gap: '12px', flexWrap: 'wrap', marginBottom: '8px',
          }}>
            <span style={{ fontWeight: 600, color: '#4a5568', fontSize: '15px' }}>
              Realizacja listy (kg){filterWeek ? ` — tydzień ${filterWeek}` : ' — wszystkie tygodnie'}
            </span>
            <span style={{ fontSize: '20px', fontWeight: 700, color: '#667eea' }}>
              {(data.completion?.percent ?? 0).toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%
            </span>
          </div>
          <div style={{ height: '14px', backgroundColor: '#edf2f7', borderRadius: '7px', overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(data.completion?.percent ?? 0, 100)}%`, height: '100%',
              backgroundColor: '#667eea', transition: 'width 0.3s',
            }} />
          </div>
          <div style={{ marginTop: '6px', fontSize: '13px', color: '#718096' }}>
            {formatKg(data.completion?.produced_kg ?? 0)} kg z {formatKg(data.completion?.planned_kg ?? 0)} kg
          </div>
        </div>
      )}

      {data.available_weeks && data.available_weeks.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <label style={{ fontWeight: '500', color: '#4a5568', fontSize: '16px' }}>
              📅 Wybierz tydzień:
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setFilterWeek('')}
              style={{
                padding: '12px 18px',
                border: filterWeek === '' ? '3px solid #667eea' : '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: filterWeek === '' ? 600 : 400,
                backgroundColor: filterWeek === '' ? '#e7f3ff' : 'white',
                color: filterWeek === '' ? '#667eea' : '#4a5568',
                cursor: 'pointer',
              }}
            >
              Wszystkie
            </button>
            {data.available_weeks
              .map(({ week, is_closed, is_empty }) => (
              <button
                key={week}
                type="button"
                onClick={() => setFilterWeek(week)}
                title={is_closed ? 'Tydzień ukończony w 100%' : is_empty ? 'Tydzień pusty — pozycje przeniesione' : undefined}
                style={{
                  padding: '12px 18px',
                  border: filterWeek === week
                    ? '3px solid #667eea'
                    : is_closed ? '2px solid #38a169' : '2px solid #e1e8ed',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: filterWeek === week ? 600 : 400,
                  backgroundColor: filterWeek === week
                    ? '#e7f3ff'
                    : is_closed ? '#f0fff4' : is_empty ? '#f7fafc' : 'white',
                  color: filterWeek === week
                    ? '#667eea'
                    : is_closed ? '#2f855a' : is_empty ? '#a0aec0' : '#4a5568',
                  cursor: 'pointer',
                  textDecoration: is_empty ? 'line-through' : 'none',
                  opacity: is_empty ? 0.7 : 1,
                }}
              >
                {week}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search filter */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Szukaj po indeksie lub nazwie..."
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 14px',
            border: '2px solid #e1e8ed',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#4a5568',
            outline: 'none',
          }}
        />
      </div>

      {/* Reorder mode toggle (admin/manager only) */}
      {(isManager || isAdmin) && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => { setReorderMode(!reorderMode); setSearchQuery(''); }}
            style={{
              padding: '8px 14px',
              border: reorderMode ? '2px solid #667eea' : '2px solid #e1e8ed',
              borderRadius: '8px',
              background: reorderMode ? '#667eea' : 'white',
              color: reorderMode ? 'white' : '#4a5568',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {reorderMode ? t('myProduction.reorderModeFinish') : t('myProduction.reorderModeStart')}
          </button>
          {reorderMode && (
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              {savingOrder ? t('myProduction.reorderSaving') : t('myProduction.reorderHint')}
            </span>
          )}
        </div>
      )}

      {/* Desktop Table View */}
      <div className="table-container" style={{ width: '100%', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <table className="table-responsive" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
          <colgroup>
            {reorderMode && <col style={{ width: '56px', minWidth: '56px' }} />}
            {[...Array(8)].map((_, i) => (
              <col key={`left-${i}`} style={{ width: `${columnWidthLeft}px`, minWidth: `${columnWidthLeft}px` }} />
            ))}
            <col style={{ width: '170px', minWidth: '170px' }} />
            <col style={{ width: `${columnWidthLeft}px`, minWidth: `${columnWidthLeft}px` }} />
            {/* DO PRODUKCJI */}
            <col style={{ width: '95px', minWidth: '95px' }} />
            {/* Wyprod */}
            <col style={{ width: '75px', minWidth: '75px' }} />
            {/* Pozo-stało */}
            <col style={{ width: '80px', minWidth: '80px' }} />
            {/* Z1, Z2 */}
            <col style={{ width: '55px', minWidth: '55px' }} />
            <col style={{ width: '55px', minWidth: '55px' }} />
            {/* Uwagi */}
            <col style={{ width: '40px', minWidth: '40px' }} />
          </colgroup>
          <thead>
            <tr>
              {/* Reorder arrows column header (only in reorder mode) */}
              {reorderMode && (
                <th style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold', color: '#000', textAlign: 'center', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>↕</th>
              )}
              {/* Scrollable columns on the left */}
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>Materiał</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>ŚREDNICA</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>WAGA</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>SZPULA</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>KOLOR</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>BARWNIK</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>DOZ. %</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>MARKA</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: 170, minWidth: 170, maxWidth: 255, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>NAZWA_ORYG</th>
              <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>INDEKS_KAT</th>
              {data.items.some(i => i.item.ean) && <th style={{ padding: '14px', fontSize: '10px', fontWeight: 'bold', color: '#000', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>EAN</th>}
              {/* Fixed columns on the right with sticky positioning */}
              <th style={{
                padding: '5px 8px',
                fontSize: '10px',
                fontWeight: 'bold',
                backgroundColor: '#fbbf24',
                color: '#000',
                position: 'sticky',
                top: 0,
                right: 200 + 80 + 75,
                zIndex: 4,
                width: 95,
                minWidth: 95,
                maxWidth: 95,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                borderLeft: '3px solid #000',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>DO PRODUKCJI</th>
              <th style={{
                padding: '5px 8px',
                fontSize: '10px',
                fontWeight: 'bold',
                backgroundColor: '#e5e7eb',
                color: '#000',
                position: 'sticky',
                top: 0,
                right: 200 + 80,
                zIndex: 4,
                width: 75,
                minWidth: 75,
                maxWidth: 75,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>Wyprod</th>
              <th style={{
                padding: '5px 8px',
                fontSize: '10px',
                fontWeight: 'bold',
                backgroundColor: '#3b82f6',
                color: 'white',
                position: 'sticky',
                top: 0,
                right: 200,
                zIndex: 4,
                width: 80,
                minWidth: 80,
                maxWidth: 80,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>Pozo-stało</th>
              <th style={{
                padding: '5px',
                fontSize: '10px',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                right: 90 + 55,
                zIndex: 4,
                backgroundColor: '#f8f9fa',
                color: '#000',
                width: 55,
                minWidth: 55,
                maxWidth: 55,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>Z1</th>
              <th style={{
                padding: '5px',
                fontSize: '10px',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                right: 90,
                zIndex: 4,
                backgroundColor: '#f8f9fa',
                color: '#000',
                width: 55,
                minWidth: 55,
                maxWidth: 55,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>Z2</th>
              <th style={{
                padding: '5px',
                fontSize: '14px',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                right: 0,
                zIndex: 4,
                backgroundColor: '#f8f9fa',
                width: 90,
                minWidth: 90,
                maxWidth: 90,
                boxSizing: 'border-box',
                overflow: 'hidden',
                textAlign: 'center',
                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
              }}>📋</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const filteredItems = data.items.filter((itemData) => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase().trim();
                return (itemData.item.indeks_kat || '').toLowerCase().includes(q) ||
                       (itemData.item.nazwa_oryg || '').toLowerCase().includes(q);
              });
              const rows: React.ReactNode[] = [];
              // Section totals always cover the whole list, not just the search matches.
              const sectionWeights = sectionTotalWeightsKg(data.items.map((itemData) => itemData.item));

              filteredItems.forEach((itemData, itemIndex) => {
                // Header detection: rows from Excel where production fields are empty
                if (isSectionHeaderRow(itemData.item)) {
                  const headerText = itemData.item.linia || itemData.item.material || itemData.item.nazwa_oryg || itemData.item.indeks_kat || '';
                  if (headerText) {
                    rows.push(
                      <tr
                        key={`header-${itemData.item.id}`}
                        style={{ backgroundColor: '#cbd5e1', borderBottom: '2px solid #000' }}
                      >
                        <td
                          colSpan={reorderMode ? 17 : 16}
                          style={{
                            padding: '10px 14px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#1e293b',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {headerText}
                          {` - ${t('table.sectionTotalWeight', { kg: formatKg(sectionWeights.get(itemData.item.id) ?? 0) })}`}
                        </td>
                      </tr>
                    );
                  }
                  return;
                }

              const rowColor = getRowColor(itemData);
              // Stan barwnika – świeci się tylko komórka w kolumnie Barwnik.
              // Czerwony = brak (zgłoszony problem albo oznaczenie w „Zapotrzebowaniu na barwnik"),
              // zielony = barwnik potwierdzony jako dostępny.
              const dyeProblem = itemData.assignments.some(a => isNoDyeProblem(a.status, a.notes))
                || isNoDyeProblem(null, itemData.item.uwagi)
                || itemData.item.barwnik_status === 'missing';
              const dyeAvailable = !dyeProblem && itemData.item.barwnik_status === 'available';
              // Miganie z uwagi – wybrana uwaga ma włączoną opcję migania, ale TYLKO dopóki pozycja
              // nie jest gotowa. Gdy getRowColor zwrócił zielony (#d1fae5 = w pełni wyprodukowane /
              // zakończone / przewinięte), uwaga przestaje migać i wiersz jest po prostu zielony.
              const isDone = rowColor === '#d1fae5';
              const blinkingNote = !isDone && (
                itemData.assignments.some(a => matchesBlinkingProblemType(a.notes, blinkingProblemTypeNames))
                || matchesBlinkingProblemType(itemData.item.uwagi, blinkingProblemTypeNames)
              );
              const mainAssignment = itemData.assignments[0];
              const status = mainAssignment?.status || 'pending';

              // Calculate produced and remaining quantities
              const doProdukcji = itemData.item.do_produkcji ? parseInt(itemData.item.do_produkcji) : 0;
              const zmiana1 = itemData.item.zmiana_1 ? parseInt(itemData.item.zmiana_1) : 0;
              const zmiana2 = itemData.item.zmiana_2 ? parseInt(itemData.item.zmiana_2) : 0;
              const wyprodukowano = zmiana1 + zmiana2;
              const pozostalo = Math.max(0, doProdukcji - wyprodukowano);

              rows.push(
                <tr
                  key={itemData.item.id}
                  className={blinkingNote ? 'blink-row-warning' : undefined}
                  onClick={reorderMode ? undefined : () => handleRowClick(itemData)}
                  style={{
                    backgroundColor: rowColor,
                    borderBottom: '2px solid #000',
                    cursor: reorderMode ? 'default' : 'pointer',
                    ...(blinkingNote && {
                      ['--blink-base' as any]: rowColor,
                      ['--blink-pulse' as any]: '#fcd34d',
                    }),
                  }}
                >
                  {/* Reorder arrows column (only in reorder mode) */}
                  {reorderMode && (
                    <td style={{ padding: '6px', fontSize: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '3px' }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveItem(itemIndex, -1); }}
                          disabled={itemIndex === 0 || savingOrder}
                          title={t('myProduction.moveUp')}
                          style={{
                            width: '28px', height: '22px', padding: 0, lineHeight: 1,
                            border: '1px solid #667eea', borderRadius: '4px',
                            background: 'white', color: '#667eea', fontSize: '12px',
                            cursor: (itemIndex === 0 || savingOrder) ? 'not-allowed' : 'pointer',
                            opacity: (itemIndex === 0 || savingOrder) ? 0.35 : 1,
                          }}
                        >▲</button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveItem(itemIndex, 1); }}
                          disabled={itemIndex === filteredItems.length - 1 || savingOrder}
                          title={t('myProduction.moveDown')}
                          style={{
                            width: '28px', height: '22px', padding: 0, lineHeight: 1,
                            border: '1px solid #667eea', borderRadius: '4px',
                            background: 'white', color: '#667eea', fontSize: '12px',
                            cursor: (itemIndex === filteredItems.length - 1 || savingOrder) ? 'not-allowed' : 'pointer',
                            opacity: (itemIndex === filteredItems.length - 1 || savingOrder) ? 0.35 : 1,
                          }}
                        >▼</button>
                      </span>
                    </td>
                  )}
                  {/* Scrollable columns */}
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.material || '-'}</td>
                  <td style={{ padding: '10px', fontSize: '10px', color: (itemData.item.srednica && (String(itemData.item.srednica).includes('2.85') || parseFloat(String(itemData.item.srednica)) === 2.85)) ? '#dc3545' : '#000', fontWeight: 'bold', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.srednica || '-'}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.waga || '-'}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.szpula || '-'}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', whiteSpace: (itemData.item.kolor && itemData.item.kolor.includes('\n')) ? 'normal' : undefined, lineHeight: '1.4' }}>{(itemData.item.kolor && itemData.item.kolor.includes('\n')) ? itemData.item.kolor.split('\n').map((line: string, i: number, arr: string[]) => <div key={i} style={{ borderBottom: i < arr.length - 1 ? '1px solid #dee2e6' : 'none', padding: '2px 0' }}>{line}</div>) : (itemData.item.kolor || '-')}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', whiteSpace: (itemData.item.barwnik && itemData.item.barwnik.includes('\n')) ? 'normal' : undefined, lineHeight: '1.4', backgroundColor: dyeProblem ? '#f8d7da' : dyeAvailable ? '#c3e6cb' : undefined, fontWeight: (dyeProblem || dyeAvailable) ? 'bold' : undefined }}>{(itemData.item.barwnik && itemData.item.barwnik.includes('\n')) ? itemData.item.barwnik.split('\n').map((line: string, i: number, arr: string[]) => <div key={i} style={{ borderBottom: i < arr.length - 1 ? '1px solid #dee2e6' : 'none', padding: '2px 0' }}>{line}</div>) : (itemData.item.barwnik || '-')}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', whiteSpace: (itemData.item.doz_procent && itemData.item.doz_procent.includes('\n')) ? 'normal' : undefined, lineHeight: '1.4' }}>{(itemData.item.doz_procent && itemData.item.doz_procent.includes('\n')) ? itemData.item.doz_procent.split('\n').map((line: string, i: number, arr: string[]) => <div key={i} style={{ borderBottom: i < arr.length - 1 ? '1px solid #dee2e6' : 'none', padding: '2px 0' }}>{line}</div>) : (itemData.item.doz_procent || '-')}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.marka || '-'}</td>
                  <td style={{
                    padding: '10px',
                    fontSize: '10px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    width: 170,
                    minWidth: 170,
                    maxWidth: 255,
                    lineHeight: '1.4'
                  }}>{itemData.item.nazwa_oryg || '-'}</td>
                  <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.indeks_kat || '-'}</td>
                  {data.items.some(i => i.item.ean) && <td style={{ padding: '10px', fontSize: '10px', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemData.item.ean || '-'}</td>}

                  {/* Fixed sticky columns on the right */}
                  <td style={{
                    padding: '5px 8px',
                    fontSize: '10px',
                    backgroundColor: '#fbbf24',
                    color: '#000',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    position: 'sticky',
                    right: 200 + 80 + 75,
                    zIndex: 1,
                    width: 95,
                    minWidth: 95,
                    maxWidth: 95,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    borderLeft: '3px solid #000'
                  }}>{itemData.item.do_produkcji || '-'}</td>
                  <td style={{
                    fontWeight: 'bold',
                    backgroundColor: '#e5e7eb',
                    color: '#000',
                    textAlign: 'center',
                    fontSize: '10px',
                    padding: '5px 8px',
                    position: 'sticky',
                    right: 200 + 80,
                    zIndex: 1,
                    width: 75,
                    minWidth: 75,
                    maxWidth: 75,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    {wyprodukowano}
                  </td>
                  <td style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    fontWeight: '600',
                    textAlign: 'center',
                    fontSize: '10px',
                    padding: '5px 8px',
                    position: 'sticky',
                    right: 200,
                    zIndex: 1,
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    {pozostalo}
                  </td>
                  <td style={{
                    fontWeight: itemData.item.zmiana_1 ? 'bold' : 'normal',
                    color: itemData.item.zmiana_1 ? '#10b981' : '#6c757d',
                    padding: '5px',
                    fontSize: '10px',
                    textAlign: 'center',
                    position: 'sticky',
                    right: 90 + 55,
                    zIndex: 1,
                    backgroundColor: '#ffffff',
                    width: 55,
                    minWidth: 55,
                    maxWidth: 55,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    {itemData.item.zmiana_1 || '0'}
                  </td>
                  <td style={{
                    fontWeight: itemData.item.zmiana_2 ? 'bold' : 'normal',
                    color: itemData.item.zmiana_2 ? '#10b981' : '#6c757d',
                    padding: '5px',
                    fontSize: '10px',
                    textAlign: 'center',
                    position: 'sticky',
                    right: 90,
                    zIndex: 1,
                    backgroundColor: '#ffffff',
                    width: 55,
                    minWidth: 55,
                    maxWidth: 55,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    {itemData.item.zmiana_2 || '0'}
                  </td>
                  {(() => {
                    const hasNote = !!(itemData.assignments.some(a => a.notes) || itemData.item.uwagi);
                    return (
                      <td
                        className={hasNote ? 'notes-cell-warning' : undefined}
                        style={{
                          padding: '5px',
                          fontSize: '10px',
                          position: 'sticky',
                          right: 0,
                          zIndex: 1,
                          backgroundColor: hasNote ? '#fde047' : '#ffffff',
                          width: 90,
                          minWidth: 90,
                          maxWidth: 90,
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          {isManager && (
                            <span
                              role="button"
                              title={t('common.edit')}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditItem(itemData);
                              }}
                              style={{ fontSize: '16px', cursor: 'pointer', lineHeight: 1, padding: '6px 4px', borderRadius: 4 }}
                            >
                              ✏️
                            </span>
                          )}
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotesClick(itemData);
                            }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', lineHeight: 1, padding: '6px 4px', borderRadius: 4 }}
                            title={
                              itemData.assignments.find(a => a.notes)?.notes || itemData.item.uwagi || ''
                            }
                          >
                            {hasNote ? (
                              <span className="notes-icon-warning" style={{ fontSize: isManager ? '20px' : '22px' }}>⚠️</span>
                            ) : (
                              <span style={{ fontSize: isManager ? '16px' : '18px', color: '#9ca3af' }}>➕</span>
                            )}
                          </span>
                          {isAdmin && (
                            <span
                              role="button"
                              title={t('productionItemDelete.deleteTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteItem(itemData.item);
                              }}
                              style={{ fontSize: '16px', cursor: 'pointer', lineHeight: 1, padding: '6px 4px', borderRadius: 4 }}
                            >
                              🗑️
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })()}
                </tr>
              );

              }); // end forEach
              return rows;
            })()}
          </tbody>
        </table>
      </div>

      {/* Tablet/Mobile Card View */}
      <div className="card-view">
        {(() => {
          const filteredCards = data.items.filter((itemData) => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase().trim();
            return (itemData.item.indeks_kat || '').toLowerCase().includes(q) ||
                   (itemData.item.nazwa_oryg || '').toLowerCase().includes(q);
          });
          const cardRows: React.ReactNode[] = [];
          const sectionWeights = sectionTotalWeightsKg(data.items.map((itemData) => itemData.item));

          filteredCards.forEach((itemData) => {
            // Header detection: rows from Excel where production fields are empty
            if (isSectionHeaderRow(itemData.item)) {
              const headerText = itemData.item.linia || itemData.item.material || itemData.item.nazwa_oryg || itemData.item.indeks_kat || '';
              if (headerText) {
                cardRows.push(
                  <div key={`header-card-${itemData.item.id}`} className="table-card" style={{ backgroundColor: '#cbd5e1' }}>
                    <div className="table-card-header">
                      <h3 style={{ margin: 0, color: '#1e293b', fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                        {headerText}
                        {` - ${t('table.sectionTotalWeight', { kg: formatKg(sectionWeights.get(itemData.item.id) ?? 0) })}`}
                      </h3>
                    </div>
                  </div>
                );
              }
              return;
            }

          cardRows.push(
            <div key={itemData.item.id} className="table-card" style={{ backgroundColor: getRowColor(itemData) }}>
              <div className="table-card-header">
                <h3 style={{ margin: 0, color: '#667eea' }}>
                  {`Pozycja #${itemData.item.id}`}
                </h3>
              </div>
              <>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Linia</div>
                  <div className="table-card-value">{itemData.item.linia || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Tydzień</div>
                  <div className="table-card-value">{itemData.assignments.map(a => a.week).join(', ')}</div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Materiał</div>
                  <div className="table-card-value">{itemData.item.material || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Średnica</div>
                  <div className="table-card-value" style={{ color: '#dc3545', fontWeight: 'bold' }}>{itemData.item.srednica || '-'}</div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Waga</div>
                  <div className="table-card-value">{itemData.item.waga || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Szpula</div>
                  <div className="table-card-value">{itemData.item.szpula || '-'}</div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Kolor</div>
                  <div className="table-card-value">{(itemData.item.kolor && itemData.item.kolor.includes('\n')) ? itemData.item.kolor.split('\n').map((line: string, i: number) => <div key={i}>{line}</div>) : (itemData.item.kolor || '-')}</div>
                </div>
                <div>
                  <div className="table-card-label">Barwnik</div>
                  <div className="table-card-value">{(itemData.item.barwnik && itemData.item.barwnik.includes('\n')) ? itemData.item.barwnik.split('\n').map((line: string, i: number) => <div key={i}>{line}</div>) : (itemData.item.barwnik || '-')}</div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">DOZ. %</div>
                  <div className="table-card-value">{(itemData.item.doz_procent && itemData.item.doz_procent.includes('\n')) ? itemData.item.doz_procent.split('\n').map((line: string, i: number) => <div key={i}>{line}</div>) : (itemData.item.doz_procent || '-')}</div>
                </div>
                <div>
                  <div className="table-card-label">Marka</div>
                  <div className="table-card-value">{itemData.item.marka || '-'}</div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Nazwa Oryg.</div>
                  <div className="table-card-value">{itemData.item.nazwa_oryg || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Indeks Kat.</div>
                  <div className="table-card-value">{itemData.item.indeks_kat || '-'}</div>
                </div>
              </div>
              {itemData.item.ean && (
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">EAN</div>
                  <div className="table-card-value">{itemData.item.ean}</div>
                </div>
              </div>
              )}
              <div className="table-card-row">
                <div>
                  <div className="table-card-label">Do Produkcji</div>
                  <div className="table-card-value">{itemData.item.do_produkcji || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Wyprod</div>
                  <div className="table-card-value" style={{ fontWeight: 'bold', color: '#10b981' }}>
                    {(() => {
                      const zmiana1 = itemData.item.zmiana_1 ? parseInt(itemData.item.zmiana_1) : 0;
                      const zmiana2 = itemData.item.zmiana_2 ? parseInt(itemData.item.zmiana_2) : 0;
                      return zmiana1 + zmiana2;
                    })()}
                  </div>
                </div>
              </div>
              <div className="table-card-row">
                <div>
                  <div className="table-card-label" style={{ backgroundColor: '#3b82f6', color: 'white', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                    Pozo-stało
                  </div>
                  <div className="table-card-value" style={{ backgroundColor: '#3b82f6', color: 'white', fontWeight: '600', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
                    {(() => {
                      const doProdukcji = itemData.item.do_produkcji ? parseInt(itemData.item.do_produkcji) : 0;
                      const zmiana1 = itemData.item.zmiana_1 ? parseInt(itemData.item.zmiana_1) : 0;
                      const zmiana2 = itemData.item.zmiana_2 ? parseInt(itemData.item.zmiana_2) : 0;
                      return Math.max(0, doProdukcji - zmiana1 - zmiana2);
                    })()}
                  </div>
                </div>
                <div>
                  <div className="table-card-label">Z1</div>
                  <div className="table-card-value">{itemData.item.zmiana_1 || '-'}</div>
                </div>
                <div>
                  <div className="table-card-label">Z2</div>
                  <div className="table-card-value">{itemData.item.zmiana_2 || '-'}</div>
                </div>
              </div>
              {(itemData.assignments.some(a => a.notes) || itemData.item.uwagi) && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e1e8ed' }}>
                <div className="table-card-label">Uwagi</div>
                <div className="table-card-value">
                  {itemData.assignments.map((assignment, idx) => (
                    <div key={idx} style={{ marginBottom: '5px' }}>
                      {assignment.notes && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <strong>Tydzień {assignment.week}:</strong> {assignment.notes}
                        </div>
                      )}
                    </div>
                  ))}
                  {!itemData.assignments.some(a => a.notes) && itemData.item.uwagi}
                </div>
              </div>
              )}
              </>
            </div>
          );

          }); // end forEach
          return cardRows;
        })()}
      </div>

      <div style={{ marginTop: '24px', padding: '16px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
        <p style={{ margin: 0, color: '#4a5568', fontSize: '10px', textAlign: 'center' }}>
          📈 <strong>Liczba pozycji:</strong> {data.items.length} | 
          ⚪ Białe = Oczekujące | 
          🟡 Żółte = W trakcie | 
          🟢 Zielone = Zakończone
        </p>
      </div>

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '720px',
              width: '92%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea' }}>{t('productionItemEdit.title')}</h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
              {EDITABLE_FIELDS.map((field) => {
                const isUwagi = field === 'uwagi';
                const isLinia = field === 'linia';
                const isTydzien = field === 'tydzien';
                return (
                  <div key={field} style={{ gridColumn: isUwagi ? '1 / -1' : 'auto' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '4px' }}>
                      {t(`productionItemEdit.fields.${field}`)}
                      {(isLinia || isTydzien) && <span style={{ color: '#dc2626', marginLeft: '2px' }}>*</span>}
                    </label>
                    {isUwagi ? (
                      <textarea
                        value={editForm[field] ?? ''}
                        onChange={(e) => handleEditFieldChange(field, e.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: '8px', border: '1px solid #e1e8ed', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    ) : isLinia ? (
                      <select
                        value={editForm[field] ?? ''}
                        onChange={(e) => handleEditFieldChange(field, e.target.value)}
                        required
                        style={{ width: '100%', padding: '8px', border: `1px solid ${(!editForm[field] || String(editForm[field]).trim() === '') ? '#dc2626' : '#e1e8ed'}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', backgroundColor: 'white' }}
                      >
                        <option value="" disabled>{t('productionItemEdit.selectLinePlaceholder')}</option>
                        {(() => {
                          const current = editForm[field] ?? '';
                          const options = productionLines.map(l => l.name);
                          if (current && !options.includes(current)) options.unshift(current);
                          return options.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ));
                        })()}
                      </select>
                    ) : isTydzien ? (
                      <select
                        value={editForm[field] ?? ''}
                        onChange={(e) => handleEditFieldChange(field, e.target.value)}
                        disabled={editWeeksLoading}
                        required
                        style={{ width: '100%', padding: '8px', border: `1px solid ${(!editForm[field] || String(editForm[field]).trim() === '') ? '#dc2626' : '#e1e8ed'}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', backgroundColor: 'white' }}
                      >
                        <option value="">
                          {editWeeksLoading
                            ? t('common.loading')
                            : editAvailableWeeks.length === 0
                              ? t('productionItemEdit.noWeeksForLine')
                              : t('productionItemEdit.selectWeekPlaceholder')}
                        </option>
                        {(() => {
                          const current = editForm[field] ?? '';
                          const options = [...editAvailableWeeks];
                          if (current && !options.includes(current)) options.unshift(current);
                          return options.map(week => (
                            <option key={week} value={week}>{week}</option>
                          ));
                        })()}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={editForm[field] ?? ''}
                        onChange={(e) => handleEditFieldChange(field, e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #e1e8ed', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {editError && (
              <div style={{ marginTop: '14px', padding: '10px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingItem(null);
                  setEditError(null);
                }}
                disabled={editSaving}
                style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: editSaving ? 'not-allowed' : 'pointer', opacity: editSaving ? 0.6 : 1 }}
              >
                {t('common.cancel')}
              </button>
              {(() => {
                const liniaEmpty = !editForm.linia || String(editForm.linia).trim() === '';
                const tydzienEmpty = !editForm.tydzien || String(editForm.tydzien).trim() === '';
                const disabled = editSaving || liniaEmpty || tydzienEmpty;
                return (
                  <button
                    onClick={handleSaveEdit}
                    disabled={disabled}
                    style={{ padding: '10px 20px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                  >
                    {editSaving ? t('common.saving') : t('common.save')}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Item Modal (admin only): PIN -> confirm */}
      {deletingItem && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: 'white', padding: '30px', borderRadius: '12px',
              maxWidth: '440px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>
              🗑️ {t('productionItemDelete.deleteTitle')}
            </h3>
            <p style={{ color: '#4a5568', fontSize: '14px' }}>
              {t('productionItemDelete.itemLabel')}: <strong>{deletingItem.indeks_kat || deletingItem.nazwa_oryg || '-'}</strong>
            </p>

            {deleteStep === 'pin' ? (
              <>
                <p style={{ color: '#4a5568', fontSize: '14px', marginTop: 16 }}>
                  {t('productionItemDelete.enterPin')}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  value={deletePin}
                  onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleDeletePinNext(); }}
                  placeholder="------"
                  maxLength={6}
                  autoFocus
                  autoComplete="off"
                  style={{
                    width: '100%', padding: '12px', marginTop: 8,
                    border: '2px solid #e1e8ed', borderRadius: '8px',
                    fontSize: '22px', letterSpacing: '8px', textAlign: 'center', fontFamily: 'monospace',
                  }}
                />
                {deleteError && (
                  <div style={{ marginTop: 10, color: '#dc3545', fontSize: 13 }}>{deleteError}</div>
                )}
                <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={closeDeleteItem} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleDeletePinNext}
                    disabled={deletePin.length !== 6}
                    style={{ padding: '10px 20px', backgroundColor: deletePin.length === 6 ? '#dc3545' : '#e9a3a3', color: 'white', border: 'none', borderRadius: 8, cursor: deletePin.length === 6 ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                  >
                    {t('common.next')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginTop: 16, padding: 14, backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8, color: '#721c24', fontSize: 14, fontWeight: 600 }}>
                  {t('productionItemDelete.confirmWarning')}
                </div>
                {deleteError && (
                  <div style={{ marginTop: 10, color: '#dc3545', fontSize: 13 }}>{deleteError}</div>
                )}
                <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={closeDeleteItem} disabled={deleting} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 8, cursor: deleting ? 'not-allowed' : 'pointer' }}>
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: 8, cursor: deleting ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: deleting ? 0.6 : 1 }}
                  >
                    {deleting ? t('common.loading') : t('productionItemDelete.confirmButton')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && editingNotes && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#667eea' }}>Uwagi</h3>

            {/* Current note */}
            {(() => {
              const currentItemData = data?.items.find(i => i.item.id === editingNotes.itemId);
              const currentNote = currentItemData?.assignments.find(a => a.notes)?.notes || currentItemData?.item.uwagi;
              if (currentNote) {
                return (
                  <div style={{
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #f59e0b'
                  }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '14px' }}>
                      ⚠️ Aktualna uwaga:
                    </label>
                    <div style={{ color: '#78350f', fontSize: '15px', fontWeight: '500' }}>
                      {currentNote}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', color: '#4a5568' }}>
                Wybierz problem:
              </label>
              {problemTypes.length === 0 ? (
                <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px', fontSize: '10px', color: '#856404' }}>
                  ⚠️ Brak dostępnych typów problemów. Skontaktuj się z administratorem, aby dodał typy problemów.
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    maxHeight: problemTypes.length > 4 ? '240px' : undefined,
                    overflowY: problemTypes.length > 4 ? 'auto' : undefined,
                    padding: problemTypes.length > 4 ? '4px 0' : 0,
                  }}
                >
                  {problemTypes.map((problemType) => {
                    const isActive = problemType.is_active !== false;
                    return (
                      <button
                        key={problemType.id}
                        type="button"
                        onClick={() => setSelectedProblemTypeId(selectedProblemTypeId === problemType.id ? null : problemType.id)}
                        style={{
                          padding: '14px 18px',
                          border: selectedProblemTypeId === problemType.id ? '3px solid #667eea' : '2px solid #e1e8ed',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: selectedProblemTypeId === problemType.id ? 600 : 400,
                          backgroundColor: selectedProblemTypeId === problemType.id ? '#e7f3ff' : (isActive ? 'white' : '#f9fafb'),
                          color: selectedProblemTypeId === problemType.id ? '#667eea' : (isActive ? '#4a5568' : '#9ca3af'),
                          cursor: 'pointer',
                          textAlign: 'left',
                          minWidth: '140px',
                          flex: '1 1 140px',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span style={{ display: 'block', fontWeight: 'inherit' }}>
                          {problemType.name}
                          {!isActive && (
                            <span style={{ marginLeft: '6px', fontSize: '12px', color: '#9ca3af', fontWeight: 400 }}>(nieaktywny)</span>
                          )}
                        </span>
                        {problemType.description && (
                          <span style={{ display: 'block', fontSize: '13px', color: isActive ? '#6b7280' : '#9ca3af', marginTop: '4px', fontWeight: 400 }}>
                            {problemType.description}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* History Section */}
            <div style={{ marginTop: '30px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                📋 Historia uwag
              </h4>
              {loadingNotesHistory ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#667eea' }}>
                  Ładowanie historii...
                </div>
              ) : (
                <>
                  {notesHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '10px' }}>
                      Brak historii uwag
                    </div>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e1e8ed' }}>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              Data
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              Problem
                            </th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                              Operator
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {notesHistory.map((entry, index) => (
                            <tr
                              key={entry.id || index}
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                              }}
                            >
                              <td style={{ padding: '10px', color: '#4a5568' }}>
                                {entry.created_at
                                  ? new Date(entry.created_at).toLocaleString('pl-PL', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </td>
                              <td style={{ padding: '10px', color: '#667eea', fontWeight: '600' }}>
                                {entry.problem_type?.name || entry.note || '-'}
                              </td>
                              <td style={{ padding: '10px', color: '#4a5568' }}>
                                {entry.user?.name || 'Nieznany'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* EAN Management Section */}
            <div style={{ marginTop: '24px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                🏷️ Kody EAN
              </h4>
              {itemEans.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '13px' }}>Brak przypisanych kodów EAN</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {itemEans.map(e => (
                    <span
                      key={e.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: '#e7f3ff',
                        border: '1px solid #b8d4f5',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {e.ean}
                      <button
                        type="button"
                        onClick={() => handleDeleteEan(e.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', padding: 0, fontSize: '14px', lineHeight: 1 }}
                        title="Usuń EAN"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newEanInput}
                  onChange={e => setNewEanInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddEan(); } }}
                  placeholder="Wpisz kod EAN..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddEan}
                  disabled={eanSaving || !newEanInput.trim()}
                  style={{
                    padding: '8px 16px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    opacity: eanSaving || !newEanInput.trim() ? 0.6 : 1,
                  }}
                >
                  Dodaj
                </button>
              </div>
              {eanError && (
                <p style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>{eanError}</p>
              )}
            </div>

            {/* Notatka (cicha - bez alertu) */}
            <div style={{ marginTop: '24px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568', fontSize: '15px' }}>
                📝 {t('myProduction.notatkaLabel')}
              </label>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px' }}>
                {t('myProduction.notatkaHint')}
              </div>

              {notatkiList.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {notatkiList.map((n) => (
                    <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#1e293b', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{n.tresc}</div>
                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                          {n.user?.name || '—'} · {new Date(n.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteNotatka(n.id)}
                        title={t('common.delete')}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc3545', fontSize: '16px', lineHeight: 1 }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <textarea
                value={newNotatka}
                onChange={(e) => setNewNotatka(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder={t('myProduction.notatkaPlaceholder')}
                style={{
                  width: '100%', padding: '12px', border: '2px solid #e1e8ed', borderRadius: '8px',
                  fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleAddNotatka}
                  disabled={savingNotatka || !newNotatka.trim()}
                  style={{
                    padding: '8px 18px', backgroundColor: '#10b981', color: 'white', border: 'none',
                    borderRadius: '8px', cursor: (savingNotatka || !newNotatka.trim()) ? 'not-allowed' : 'pointer', fontWeight: 600,
                    opacity: (savingNotatka || !newNotatka.trim()) ? 0.6 : 1,
                  }}
                >
                  + {t('myProduction.notatkaAdd')}
                </button>
              </div>
            </div>

            <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDeleteNotes}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Usuń uwagę
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotesModal(false);
                    setEditingNotes(null);
                    setSelectedProblemTypeId(null);
                    setNotesHistory([]);
                    setItemEans([]);
                    setNewEanInput('');
                    setEanError(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={handleSaveNotes}
                  disabled={!selectedProblemTypeId}
                  style={{
                    padding: '10px 20px',
                    background: selectedProblemTypeId ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d3d3d3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: selectedProblemTypeId ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    opacity: selectedProblemTypeId ? 1 : 0.6,
                  }}
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Start Production Modal */}
      {showStartModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, color: '#667eea', marginBottom: '20px' }}>Rozpoczęcie produkcji</h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#4a5568' }}>
              Czy rozpocząć produkcję: <strong>{selectedItem.item.nazwa_oryg || selectedItem.item.indeks_kat || 'pozycję'}</strong>?
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>Zmiana:</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={() => setSelectedShift('zmiana_1')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_1' ? '#667eea' : '#e2e8f0', color: selectedShift === 'zmiana_1' ? 'white' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Zmiana 1</button>
                <button onClick={() => setSelectedShift('zmiana_2')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_2' ? '#667eea' : '#e2e8f0', color: selectedShift === 'zmiana_2' ? 'white' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Zmiana 2</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowStartModal(false); setSelectedItem(null); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>ANULUJ</button>
              <button onClick={handleStartConfirm} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>TAK</button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Production Modal */}
      {showCompleteModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, color: '#28a745', marginBottom: '20px' }}>{t('myProduction.endProductionTitle')}</h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#4a5568' }}>
              {t('myProduction.endProductionShortPart1')}{' '}
              <strong>{selectedItem.item.nazwa_oryg || selectedItem.item.indeks_kat || t('myProduction.itemFallback')}</strong>{' '}
              {t('myProduction.endProductionPart2')}{' '}
              <strong>{selectedItem.item.do_produkcji || '0'}</strong>
              {t('myProduction.endProductionSuffix')}
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>{t('shift.fieldLabel')}</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={() => setSelectedShift('zmiana_1')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_1' ? '#28a745' : '#e2e8f0', color: selectedShift === 'zmiana_1' ? 'white' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>{t('shift.shift1')}</button>
                <button onClick={() => setSelectedShift('zmiana_2')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_2' ? '#28a745' : '#e2e8f0', color: selectedShift === 'zmiana_2' ? 'white' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>{t('shift.shift2')}</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCompleteModal(false); setShowProblemModal(true); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{t('common.no')}</button>
              <button onClick={handleCompleteConfirm} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>{t('common.yes')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Options Modal */}
      {showProblemModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, color: '#dc3545', marginBottom: '20px' }}>Opcje zakończenia</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => handleProblemOption('continue')} style={{ padding: '12px 20px', backgroundColor: '#fff3cd', color: '#856404', border: '2px solid #ffc107', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', textAlign: 'left' }}>Produkuję dalej</button>
              <button onClick={() => handleProblemOption('incomplete')} style={{ padding: '12px 20px', backgroundColor: '#fff3cd', color: '#856404', border: '2px solid #ffc107', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', textAlign: 'left' }}>Niepełna produkcja</button>
              <button onClick={() => handleProblemOption('problem_bb')} style={{ padding: '12px 20px', backgroundColor: '#f8d7da', color: '#721c24', border: '2px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', textAlign: 'left' }}>Problem BB</button>
              <button onClick={() => handleProblemOption('problem_awaria')} style={{ padding: '12px 20px', backgroundColor: '#f8d7da', color: '#721c24', border: '2px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', textAlign: 'left' }}>Problem Awaria</button>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete Production Modal */}
      {showIncompleteModal && selectedItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, color: '#ffc107', marginBottom: '20px' }}>Niepełna produkcja</h3>
            <p style={{ fontSize: '14px', marginBottom: '15px', color: '#4a5568' }}>Wpisz ile zrobiono:</p>
            <input
              type="number"
              value={incompleteQuantity}
              onChange={(e) => setIncompleteQuantity(e.target.value)}
              placeholder="0"
              min="0"
              max={selectedItem.item.do_produkcji ? parseInt(selectedItem.item.do_produkcji) : undefined}
              style={{ width: '100%', padding: '12px', border: '2px solid #e1e8ed', borderRadius: '8px', fontSize: '16px', marginBottom: '15px' }}
              autoFocus
            />
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>Zmiana:</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={() => setSelectedShift('zmiana_1')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_1' ? '#ffc107' : '#e2e8f0', color: selectedShift === 'zmiana_1' ? '#856404' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Zmiana 1</button>
                <button onClick={() => setSelectedShift('zmiana_2')} style={{ padding: '8px 16px', backgroundColor: selectedShift === 'zmiana_2' ? '#ffc107' : '#e2e8f0', color: selectedShift === 'zmiana_2' ? '#856404' : '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Zmiana 2</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowIncompleteModal(false); setIncompleteQuantity(''); setShowProblemModal(true); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Anuluj</button>
              <button onClick={handleIncompleteSave} disabled={!incompleteQuantity || parseInt(incompleteQuantity) < 0} style={{ padding: '10px 20px', backgroundColor: '#ffc107', color: '#856404', border: 'none', borderRadius: '8px', cursor: incompleteQuantity && parseInt(incompleteQuantity) >= 0 ? 'pointer' : 'not-allowed', fontWeight: '500', opacity: incompleteQuantity && parseInt(incompleteQuantity) >= 0 ? 1 : 0.5 }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel / Problem Modal */}
      {showCancelModal && selectedItem && (() => {
        const itemStatus = getItemStatus({ item: selectedItem.item, assignments: data?.items.find(i => i.item.id === selectedItem.item.id)?.assignments || [] });
        const isProblem = itemStatus === 'problem_bb' || itemStatus === 'problem_awaria';
        // "Do przewijania" only for completed (green) items that are not already waiting at the
        // station and have not been rewound yet.
        const canSendToRewinding = itemStatus === 'completed' && canMarkToRewind(selectedItem.item.rewind_entries);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              <h3 style={{ marginTop: 0, color: isProblem ? '#0d6efd' : '#dc3545', marginBottom: '20px' }}>
                {isProblem ? 'Uwaga / Problem' : 'Anulowanie produkcji'}
              </h3>
              <p style={{ fontSize: '16px', marginBottom: '15px', color: '#4a5568' }}>
                {isProblem ? 'Pozycja oznaczona jako problem:' : 'Czy na pewno chcesz anulować produkcję pozycji:'}
              </p>
              <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: '600', color: '#2d3748' }}>
                <strong>{selectedItem.item.nazwa_oryg || selectedItem.item.indeks_kat || t('common.notAvailable')}</strong>
              </p>
              {isProblem ? (
                <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', marginBottom: '20px', border: '1px solid #0d6efd' }}>
                  <p style={{ fontSize: '14px', margin: '5px 0', color: '#0a58ca' }}>
                    Możesz <strong>usunąć problem</strong> (wrócić do „W trakcie") albo <strong>anulować produkcję</strong> (reset do „Oczekujące").
                  </p>
                </div>
              ) : (
                <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffc107' }}>
                  <p style={{ fontSize: '12px', margin: '5px 0', color: '#856404' }}>Ta akcja wyczyści wartości zmian i zresetuje status do "Oczekujące".</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button onClick={() => { setShowCancelModal(false); setSelectedItem(null); }} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Zamknij</button>
                {canSendToRewinding && (
                  <button onClick={handleAddToRewinding} style={{ padding: '10px 20px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                    🔄 {t('myProduction.goToRewinding')}
                  </button>
                )}
                {isProblem && (
                  <button onClick={handleRemoveProblem} style={{ padding: '10px 20px', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Usuń problem</button>
                )}
                <button onClick={handleCancelConfirm} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                  {isProblem ? 'Anuluj produkcję' : 'Tak, anuluj'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProductionLineView;
